import { AuthContextProvider } from '@phx-husky/auth-context';
import { AuthModalsContainer } from '@phx-husky/auth-modals-container';
import { FieldCategory, InputType, Legal } from '@phx-husky/auth-service';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import camelCase from 'lodash/camelCase';
import React, { FC } from 'react';

import { getData } from '~/config/AuthConfig';
import { useCurrentLang } from '~/hooks/useCurrentLang';
import isJSON from '~/utils/isJSON';

import { AuthProps, AuthQuery } from './models';

export const Auth: FC<AuthProps> = ({ children }) => {
  const {
    currentLang: { locale: currentLangCode },
  } = useCurrentLang();
  const {
    allContentstackLdsData: { nodes: ldsDataNodes },
    allContentstackAuthSettings: { nodes: authSettingsPages },
    allContentstackLdsPage: { nodes: allLdsPages },
  } = useStaticQuery<AuthQuery>(graphql`
    {
      allContentstackAuthSettings {
        nodes {
          ...AuthSettingsFragment
        }
      }
      allContentstackLdsData {
        nodes {
          documentType
          validFrom
          validTo
          versionId
          uri
        }
      }
      allContentstackLdsPage {
        nodes {
          fields {
            ...PageFieldsFragment
          }
          lds {
            uri
          }
          tags
          publishDetails: publish_details {
            locale
          }
        }
      }
    }
  `);

  const ldsPages = allLdsPages.filter(
    ({ tags, publishDetails: { locale } }) =>
      tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
      locale === currentLangCode
  );

  const authSettings = authSettingsPages.find(
    ({ tags, publishDetails: { locale } }) =>
      tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
      locale === currentLangCode
  );

  if (!authSettings) {
    throw new Error('Auth Settings not exist');
  }

  const ldsMapped = authSettings.agreementsSettings
    .map(({ isRequired, agreementLabel, checkboxHidden, lds: [{ uri }] }) => {
      const ldsData = ldsDataNodes.find((lds) => lds.uri === uri);
      const ldsPage = ldsPages.find(
        ({ lds: [{ uri: ldsUri }] }) => ldsUri === uri
      );

      if (!ldsData || !ldsPage) {
        return null;
      }

      return {
        isRequired,
        customLabel: agreementLabel,
        hideCheckbox: checkboxHidden,
        validFrom: new Date(ldsData.validFrom),
        validTo: new Date(ldsData.validTo),
        businessId: uri,
        socialTerm: false,
        versionId: ldsData.versionId,
        key: ldsData.versionId,
        description: ldsData.documentType,
        onLegalLinkClickAction: () =>
          window.open(ldsPage.fields.fullPath, '_blank'),
      } as Legal;
    })
    .filter(Boolean) as Legal[];

  const fieldsMapped = authSettings.fieldsSettings.map((params) => {
    const {
      field,
      showInSignUp,
      showInProfile,
      showInSignIn,
      isRequired,
      showInEdit,
      showInPreview,
      orderSignin,
      orderSignup,
      orderProfile,
      isRequiredInEdit,
    } = params;

    const fieldOrder =
      orderSignin || orderSignup || orderProfile
        ? {
            order: {
              ...(orderSignin && { signIn: Number(orderSignin) }),
              ...(orderSignup && { signUp: Number(orderSignup) }),
              ...(orderProfile && { profile: Number(orderProfile) }),
            },
          }
        : null;

    if (field === InputType.custom) {
      const {
        customFieldName,
        customFieldType,
        customFieldValidationRegex,
        customFieldDefaultValue,
        ...customFieldRest
      } = params;

      const fieldNameFromCustom = camelCase(customFieldName?.toLowerCase());

      const customFieldData = {
        ...customFieldRest,
        customField: true,
        category: FieldCategory.UserDetails,
        id: fieldNameFromCustom,
        name: fieldNameFromCustom,
        type: customFieldType,
        regex: customFieldValidationRegex
          ? new RegExp(customFieldValidationRegex)
          : undefined,
        ...fieldOrder,
      };

      if (customFieldType === 'select' && customFieldDefaultValue) {
        customFieldData.options = customFieldDefaultValue
          .split(';')
          .filter(Boolean)
          .map((opt) => ({
            key: opt.trim(),
            translationKey: opt.trim(),
          }));
      }

      return customFieldData;
    }

    return {
      name: field,
      field,
      showInSignUp,
      showInProfile,
      showInSignIn,
      isRequired,
      showInEdit,
      showInPreview,
      isRequiredInEdit,
      ...fieldOrder,
    };
  });

  const customTranslations = authSettings?.translations?.customTranslations;

  const loginSettings = authSettings?.loginSettings;

  if (customTranslations && !isJSON(customTranslations)) {
    throw new Error('Please provide translations in correct json format');
  }

  const parsedTranslations = customTranslations
    ? JSON.parse(customTranslations)
    : null;

  return (
    <AuthContextProvider
      defaultValue={getData(
        ldsMapped,
        fieldsMapped,
        parsedTranslations,
        loginSettings
      )}
    >
      {children}
      <AuthModalsContainer onDeleteAccount={() => navigate('/')} />
    </AuthContextProvider>
  );
};
